import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AppText from "./AppText";
import { appStrings } from "./strings";
import "./Login.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ProductTips from "./ProductTips";
import TryMDH from "./TryMDH";
import Button from "../../presentational/Button";
import Input from "../../presentational/Input";
import { useTurnstile } from "../../../hooks/useTurnstile";

interface LoginProps {
    email: string;
    serverError: string;
    joinLink: string;
    isLoading: boolean;
    finishLogin: (password: string, clientVerificationToken: string) => Promise<void>;
    setEmail: (email: string) => void;
    forgotPassword: () => void;
    getClientVerificationToken: (form: HTMLFormElement) => Promise<string>;
}

const Login: React.FC<LoginProps> = ({
    email,
    serverError,
    joinLink,
    isLoading,
    finishLogin,
    setEmail,
    forgotPassword,
    getClientVerificationToken,
}) => {
    const [password, setPassword] = useState("");
    const [unmaskPassword, setUnmaskPassword] = useState(false);
    useTurnstile();

    const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    };

    const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.currentTarget;
        finishLogin(password, await getClientVerificationToken(form));
    };

    return (
        <main className="col-layout login-page">
            <aside className="brand-sidebar">
                <ProductTips />
                {/* <ProductNews /> */}
            </aside>
            <section className="authentication-action">
                <form className="form" onSubmit={handleSubmit}>
                    <h2 className="form-header">
                        <img className="form-header-logo" src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/mdhd-blue-bug-logo-centered.svg" alt="MyDataHelps Designer Logo" />
                        <AppText textKey="login-title" />
                    </h2>
                    {serverError && <p className="server-error">{serverError}</p>}
                    <div className="field-group">
                        <label><AppText textKey="username-label" /></label>
                        <Input
                            className="autofocus"
                            inputClassName="admin-input"
                            disabled={isLoading}
                            id="username"
                            value={email}
                            fullWidth={true}
                            onChange={handleChangeEmail}
                            type="text"
                            placeholder={appStrings["username-placeholder"]}
                            autoComplete="username"
                            autoFocus={true}
                            htmlAttributes={{
                                spellCheck: false,
                                autoCorrect: "off",
                                autoCapitalize: "none",
                            }}
                        />
                    </div>
                    <div className="field-group">
                        <label><AppText textKey="password-label" /></label>
                        <div className="combo-input-group">
                            <input
                                autoComplete="current-password"
                                className="autofocus admin-input"
                                disabled={isLoading}
                                id="password"
                                type={unmaskPassword ? "text" : "password"}
                                value={password}
                                onChange={handleChangePassword}
                                placeholder={appStrings["password-placeholder"]}
                                maxLength={64}
                            />
                            <button
                                type="button"
                                onClick={() => setUnmaskPassword(!unmaskPassword)}
                                className="password-mask-toggle editor-controls-button"
                                title="Toggle password visibility"
                            >
                                <FontAwesomeIcon
                                    icon={unmaskPassword ? (faEye as IconProp) : (faEyeSlash as IconProp)}
                                />
                            </button>
                        </div>
                    </div>
                    <div className="field-group">
                        <div id="cf-turnstile" className="cf-turnstile"></div>
                    </div>
                    <div className="form-buttons">
                        <Button
                            intent="primary"
                            disabled={!email || !password || isLoading}
                            className="primary-button editor-controls-button"
                            loading={isLoading}
                            type="submit"
                        >
                            <AppText textKey="sign-in-label" />
                        </Button>
                        <button
                            className="editor-controls-button text-button"
                            disabled={isLoading}
                            onClick={forgotPassword}
                        >
                            <AppText textKey="forgot-password-label" />
                        </button>
                    </div>

                    <Link className="editor-controls-button tertiary-button" to={joinLink}>
                        <AppText textKey="create-account-label" />
                    </Link>
                </form>
                <TryMDH />
            </section>
        </main>
    );
};

export default Login;
